/**
 * 工厂端 - 代销合作（确认/还价/拒绝邀约）
 *
 * 后端 /partnerships 对 FACTORY 返回 factory_id=自己 的合作。
 * 对 NEGOTIATING 状态：接受/还价（POST /partnerships/:id/accept）、拒绝（POST /partnerships/:id/reject）。
 */

import { useState } from 'react'
import { Card, Table, Button, Badge, useApi, time, formatScope } from '../../components/ui'
import { HelpBubble } from '../../components/HelpBubble'
import { api } from '../../api/client'

interface Partnership {
  partnership_id: string
  seller_id: string
  factory_id: string
  commission_rate: string
  product_scope: unknown
  valid_from: string
  valid_to: string
  contract_id: string | null
  status: string
  created_at: string
  signed_at: string | null
}

export function FactoryPartnerships() {
  const list = useApi<Partnership[]>(async () => api.get('/partnerships'), [])
  const [acceptingId, setAcceptingId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function reject(id: string) {
    if (!confirm('确认拒绝该合作邀约？')) return
    setBusy(true)
    try {
      await api.post(`/partnerships/${id}/reject`)
      list.refresh()
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>代销合作</h2>

      <Card title={`合作邀约（${list.data?.length ?? 0}）`}>
        <Table<Partnership>
          rows={list.data ?? []}
          empty={list.loading ? '加载中...' : '暂无合作邀约'}
          columns={[
            { key: 'partnership_id', title: '合作ID' },
            { key: 'seller_id', title: '商家' },
            {
              key: 'commission_rate',
              title: '佣金率',
              render: (r) => `${(Number(r.commission_rate) * 100).toFixed(1)}%`,
            },
            { key: 'product_scope', title: '范围', render: (r) => formatScope(r.product_scope) },
            {
              key: 'valid',
              title: '有效期',
              render: (r) => `${r.valid_from} ~ ${r.valid_to}`,
            },
            { key: 'signed_at', title: '签约时间', render: (r) => (r.signed_at ? time(r.signed_at) : '-') },
            { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
            {
              key: 'action',
              title: '操作',
              render: (r) =>
                r.status === 'NEGOTIATING' ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="sm" onClick={() => setAcceptingId(r.partnership_id)} disabled={busy}>
                      接受/还价
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => reject(r.partnership_id)} disabled={busy}>
                      拒绝
                    </Button>
                  </div>
                ) : (
                  '-'
                ),
            },
          ]}
        />
      </Card>

      {acceptingId && (
        <AcceptForm
          partnership={list.data!.find((p) => p.partnership_id === acceptingId)!}
          onCancel={() => setAcceptingId(null)}
          onDone={() => {
            setAcceptingId(null)
            list.refresh()
          }}
        />
      )}

      <HelpBubble pageKey="factory-partnerships" title="代销合作">
        <p>工厂与商家是<b>多对多</b>关系。商家发起邀约后，工厂在此<b>接受/还价/拒绝</b>。</p>
        <p>接受后合作立即生效，标准条款（所有权归工厂、平台分账、双票代开）不可改。</p>
      </HelpBubble>
    </div>
  )
}

function AcceptForm({
  partnership,
  onCancel,
  onDone,
}: {
  partnership: Partnership
  onCancel: () => void
  onDone: () => void
}) {
  const [rate, setRate] = useState<number>(Number(partnership.commission_rate))
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)

  async function submit() {
    setLoading(true)
    setErr('')
    try {
      await api.post(`/partnerships/${partnership.partnership_id}/accept`, {
        commissionRate: rate,
      })
      onDone()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card title={`接受/还价 · ${partnership.partnership_id}`}>
      <div style={{ display: 'grid', gap: 8, maxWidth: 400 }}>
        <div style={{ fontSize: 13, color: '#666' }}>
          商家：{partnership.seller_id} · 原佣金率：{(Number(partnership.commission_rate) * 100).toFixed(1)}%
        </div>
        <label>
          佣金率(0-1)：
          <input
            type="number"
            step="0.01"
            min={0}
            max={1}
            value={rate}
            onChange={(e) => setRate(parseFloat(e.target.value))}
            style={inputStyle}
          />
        </label>
        <div style={{ color: '#888', fontSize: 12 }}>
          提示：还价后合作立即生效，标准合同条款（所有权归工厂、平台分账、双票代开）不可改。
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={submit} disabled={loading}>
            {loading ? '提交中...' : '确认接受'}
          </Button>
          <Button variant="default" onClick={onCancel}>
            取消
          </Button>
        </div>
        {err && <div style={{ color: '#dc2626' }}>{err}</div>}
      </div>
    </Card>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  width: '100%',
  marginLeft: 8,
}
